import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { publicApi } from "@/lib/api";

const FORMATS = ["La Table (dinner)", "Le Salon (music / talk)", "L'Atelier (workshop)", "Not sure yet"];

export default function HostApply() {
  const [form, setForm] = useState({ venue_name: "", host_name: "", email: "", location: "", format: FORMATS[0], message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicApi.post("/venue-applications", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send. Check your details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="host-apply-page" className="pt-32 pb-24 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div className="lg:sticky lg:top-28">
          <div className="eyebrow mb-3">Host with us</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-wine leading-tight mb-6">
            Open your doors for one unforgettable evening.
          </h1>
          <p className="text-ink-muted leading-relaxed mb-6">
            Tell us about your house. If it's the right fit, we'll be in touch to design an evening together — you provide the setting and the welcome, we handle guests, booking and payment.
          </p>
          <ul className="space-y-3 text-sm text-ink-muted">
            {["No marketing, no platform fees to start", "You keep control of dates and numbers", "A curated table of respectful guests", "A new revenue night for your heritage"].map((t) => (
              <li key={t} className="flex items-start gap-3"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />{t}</li>
            ))}
          </ul>
        </div>

        <div className="card-parchment p-8">
          {sent ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="apply-success" className="text-center py-10">
              <Check className="w-12 h-12 text-gold mx-auto flicker" />
              <h2 className="font-serif text-3xl text-wine mt-6">Merci.</h2>
              <p className="text-ink-muted mt-3">Your proposal has reached us. We read every one and reply to the houses that fit.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
              <Field label="Name of the venue" testid="apply-venue"><input required value={form.venue_name} onChange={set("venue_name")} className="field" placeholder="Château de…" /></Field>
              <Field label="Your name" testid="apply-host"><input required value={form.host_name} onChange={set("host_name")} className="field" placeholder="Hélène de…" /></Field>
              <Field label="Email" testid="apply-email"><input required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@house.fr" /></Field>
              <Field label="Where is it?" testid="apply-location"><input required value={form.location} onChange={set("location")} className="field" placeholder="Paris 7e / Fontainebleau…" /></Field>
              <Field label="What kind of evening?" testid="apply-format">
                <select value={form.format} onChange={set("format")} className="field" data-testid="apply-format-select">
                  {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Tell us about the house" testid="apply-message">
                <textarea rows={4} value={form.message} onChange={set("message")} className="field resize-none" placeholder="Its history, its rooms, what makes an evening there special…" />
              </Field>
              <button type="submit" disabled={busy} data-testid="apply-submit-btn" className="btn-gold w-full py-4 text-sm font-semibold tracking-wide disabled:opacity-60">
                {busy ? "Sending…" : "Send my proposal"}
              </button>
            </form>
          )}
        </div>
      </div>
      <style>{`.field{width:100%;background:#FAF6F0;border:1px solid var(--stone-border);padding:0.7rem 0.9rem;font-size:0.9rem;color:var(--ink);outline:none;transition:border-color .3s}.field:focus{border-color:var(--gold)}`}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}
